import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { Info, Check, X } from 'lucide-react';
import { Fragment } from 'react';

// Capability matrix mirrors the backend `require_role()` enforcement in /app/backend/routes/.
// Source of truth: viewer < member < manager < admin. (super_admin is platform operator only.)
const ROLES = ['viewer', 'member', 'manager', 'admin'];

const CAPABILITY_GROUPS = [
  {
    label: 'Claims',
    rows: [
      ['View dashboard, claims, all entries',         [true,  true,  true,  true]],
      ['Create / edit claims, notes, file uploads',   [false, true,  true,  true]],
      ['Delete / restore claims (Recycle Bin)',       [false, false, true,  true]],
    ],
  },
  {
    label: 'Companies',
    rows: [
      ['View companies',                              [true,  true,  true,  true]],
      ['Create a new company',                        [false, false, true,  true]],
      ['Edit / delete company, manage policies',      [false, false, false, true]],
    ],
  },
  {
    label: 'Users & Invites',
    rows: [
      ['Invite users, change roles, deactivate',      [false, false, false, true]],
    ],
  },
  {
    label: 'Organizations  (Provider only)',
    rows: [
      ['Switch org context via the switcher',         [true,  true,  true,  true]],
      ['Create / edit / delete an organization',      [false, false, false, true]],
      ['Link / unlink Solo tenants',                  [false, false, false, true]],
    ],
  },
  {
    label: 'Billing',
    rows: [
      ['View billing, manage subscription',           [false, false, false, true]],
    ],
  },
  {
    label: 'Tenant Settings',
    rows: [
      ['Edit tenant name, logo, defaults',            [false, false, false, true]],
    ],
  },
];

const ROLE_LABEL = { viewer: 'Viewer', member: 'Member', manager: 'Manager', admin: 'Admin' };
const ROLE_COLOR = {
  viewer:  'text-gray-600',
  member:  'text-green-700',
  manager: 'text-blue-700',
  admin:   'text-purple-700',
};

function Cell({ allowed }) {
  return allowed
    ? <Check className="w-3.5 h-3.5 text-emerald-500 mx-auto" />
    : <X className="w-3.5 h-3.5 text-gray-300 mx-auto" />;
}

/**
 * Hover-card info icon that reveals the full role permission matrix.
 * Pure presentational — drop it next to any "Role" label or selector.
 */
export default function RolePermissionsTooltip({ className = '' }) {
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors ${className}`}
          aria-label="Show role permissions"
          data-testid="role-permissions-info"
          onClick={(e) => e.preventDefault()}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={6}
        className="w-[520px] max-w-[92vw] p-0 overflow-hidden"
        data-testid="role-permissions-card"
      >
        <div className="px-4 py-2.5 border-b bg-gray-50">
          <p className="text-xs font-semibold text-gray-700">Role permissions</p>
          <p className="text-[11px] text-gray-500 mt-0.5">What each role can do. Higher roles inherit everything below.</p>
        </div>
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-white sticky top-0 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500 w-[260px]">Capability</th>
                {ROLES.map((r) => (
                  <th key={r} className={`px-2 py-2 font-semibold text-center ${ROLE_COLOR[r]}`}>
                    {ROLE_LABEL[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITY_GROUPS.map((group) => (
                <Fragment key={group.label}>
                  <tr className="bg-gray-50/70">
                    <td colSpan={5} className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {group.label}
                    </td>
                  </tr>
                  {group.rows.map(([label, allowed], i) => (
                    <tr key={`${group.label}-${i}`} className="border-t">
                      <td className="px-3 py-1.5 text-gray-700">{label}</td>
                      {allowed.map((ok, j) => (
                        <td key={j} className="px-2 py-1.5"><Cell allowed={ok} /></td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
